<template>
  <div class="ds-toolbar">
    <div class="ds-toolbar__left">
      <slot name="left"></slot>
      <span class="ds-toolbar__total" v-if="total !== null && total !== undefined">
        共 <strong>{{ total }}</strong> 条
      </span>
    </div>

    <div class="ds-toolbar__right">
      <slot name="right"></slot>

      <!-- 批量操作提示条：仅在有选中项时出现 -->
      <transition name="ds-fade">
        <div class="ds-toolbar__selection" v-if="selected > 0">
          <i class="el-icon-check"></i>
          已选 <strong>{{ selected }}</strong> 项
          <slot name="batch"></slot>
        </div>
      </transition>

      <!-- 列设置 -->
      <el-popover
        v-if="showColumnSetting && columns.length"
        placement="bottom-end"
        width="220"
        trigger="click">
        <div class="ds-column-setting">
          <div class="ds-column-setting__title">显示列</div>
          <el-checkbox-group v-model="innerVisible">
            <el-checkbox
              v-for="col in columns"
              :key="col.prop"
              :label="col.prop"
              :disabled="col.fixed === 'left' || col.fixed === 'right'">
              {{ col.label }}
            </el-checkbox>
          </el-checkbox-group>
        </div>
        <el-button slot="reference" size="mini" icon="el-icon-s-grid">
          列设置
        </el-button>
      </el-popover>

      <!-- 密度切换 -->
      <el-button
        v-if="showDensity"
        size="mini"
        icon="el-icon-rank"
        :title="density === 'compact' ? '切换为宽松行高' : '切换为紧凑行高'"
        @click="toggleDensity">
        {{ density === 'compact' ? '紧凑' : '宽松' }}
      </el-button>

      <!-- 刷新 -->
      <el-button v-if="showRefresh" size="mini" icon="el-icon-refresh" @click="$emit('refresh')">
        刷新
      </el-button>
    </div>
  </div>
</template>

<script>
/**
 * DsToolbar —— 列表工具条
 *
 * 提供结果摘要、列设置、密度切换、刷新、批量操作入口。
 * **不改变任何数据加载逻辑**，所有动作均通过事件向父组件抛出。
 *
 * Props:
 *  - total            {Number}  结果总数，用于摘要；null 表示不显示
 *  - selected         {Number}  当前选中条数
 *  - columns          {Array}   列定义 [{ prop, label, fixed }]，用于列设置
 *  - density          {String}  'default' | 'compact'
 *  - showColumnSetting{Boolean} 是否显示列设置
 *  - showDensity      {Boolean} 是否显示密度切换
 *  - showRefresh      {Boolean} 是否显示刷新按钮
 *
 * Events:
 *  - update:columns   可见列变化
 *  - update:density   密度变化
 *  - refresh          点击刷新
 */
export default {
  name: 'DsToolbar',
  props: {
    total: {
      type: [Number, null],
      default: null
    },
    selected: {
      type: Number,
      default: 0
    },
    columns: {
      type: Array,
      default: () => []
    },
    density: {
      type: String,
      default: 'default'
    },
    showColumnSetting: {
      type: Boolean,
      default: true
    },
    showDensity: {
      type: Boolean,
      default: true
    },
    showRefresh: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      innerVisible: this.columns.map(c => c.prop)
    }
  },
  watch: {
    columns: {
      deep: true,
      handler (val) {
        const valid = val.map(c => c.prop)
        this.innerVisible = this.innerVisible.filter(p => valid.indexOf(p) > -1)
      }
    },
    'innerVisible' (val) {
      this.$emit('update:columns', val)
    }
  },
  methods: {
    toggleDensity () {
      this.$emit('update:density', this.density === 'compact' ? 'default' : 'compact')
    }
  }
}
</script>

<style lang="scss" scoped>
.ds-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--ds-spacing-md);
  flex-wrap: wrap;
  padding: var(--ds-spacing-sm) var(--ds-spacing-lg);
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-bottom: none;
  border-radius: var(--ds-radius-md) var(--ds-radius-md) 0 0;

  &__left {
    display: flex;
    align-items: center;
    gap: var(--ds-spacing-md);
    min-width: 0;
  }

  &__right {
    display: flex;
    align-items: center;
    gap: var(--ds-spacing-sm);
    flex-shrink: 0;
  }

  &__total {
    font-size: var(--ds-font-size-sm);
    color: var(--ds-gray-500);

    strong {
      color: var(--ds-gray-800);
      font-weight: 600;
    }
  }

  &__selection {
    display: inline-flex;
    align-items: center;
    gap: var(--ds-spacing-xs);
    height: 28px;
    padding: 0 var(--ds-spacing-sm);
    font-size: var(--ds-font-size-sm);
    color: var(--ds-primary);
    background: var(--ds-primary-sub);
    border-radius: var(--ds-radius-sm);

    strong {
      font-weight: 700;
    }
  }
}

.ds-column-setting {
  &__title {
    font-size: var(--ds-font-size-sm);
    font-weight: 600;
    color: var(--ds-gray-700);
    margin-bottom: var(--ds-spacing-sm);
    padding-bottom: var(--ds-spacing-xs);
    border-bottom: 1px solid var(--ds-gray-200);
  }

  .el-checkbox-group {
    display: flex;
    flex-direction: column;
    max-height: 260px;
    overflow-y: auto;
  }

  .el-checkbox {
    margin-right: 0;
    padding: var(--ds-spacing-xs) 0;
  }
}

.ds-fade-enter-active,
.ds-fade-leave-active {
  transition: opacity var(--ds-transition-fast);
}

.ds-fade-enter,
.ds-fade-leave-to {
  opacity: 0;
}
</style>
